import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Loader2, XCircle, Coins, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("failed");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 15) {
      setStatus("failed");
      toast.error("Payment verification timed out.");
      return;
    }

    try {
      const res = await axios.get(`${API}/credits/checkout/status/${sessionId}`, {
        headers: getHeaders(),
        withCredentials: true
      });

      if (res.data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(res.data);
        toast.success(`${res.data.credits} AI added to your account!`);
        return;
      }

      if (res.data.status === "expired") {
        setStatus("failed");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      console.error("Payment status check failed");
      if (attempts >= 5) {
        setStatus("failed");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-8 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 text-emerald-400 mx-auto mb-6 animate-spin" />
              <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-white mb-2">
                VERIFYING PAYMENT
              </h1>
              <p className="text-slate-400">Please wait while we confirm your purchase...</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-white mb-2">
                PAYMENT SUCCESSFUL!
              </h1>
                <p className="text-slate-400 mb-6">Your AI has been added to your account</p>
              
              {paymentData && (
                <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-center gap-2 text-2xl font-bold text-emerald-400">
                    <Coins className="w-6 h-6" />
                    +{paymentData.credits} {paymentData.credits === 1 ? "AI" : "AIs"}
                  </div>
                  <p className="text-slate-500 text-sm mt-2">
                    New balance: {paymentData.balance} {paymentData.balance === 1 ? "AI" : "AIs"}
                  </p>
                </div>
              )}
              
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate("/comms-centre/meetings")}
                  variant="outline"
                  className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  Start a Meeting
                </Button>
              </div>
            </>
          )}

          {status === "failed" && (
            <>
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-white mb-2">
                PAYMENT ISSUE
              </h1>
              <p className="text-slate-400 mb-6">
                We couldn't verify your payment. If you were charged, please contact support.
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/aipps")}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
